import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

// ─── Design tokens ───────────────────────────────────────────────────────
const T = {
  primary: '#0E6735', primary600: '#0A5229', primary50: '#E9F3EC', primary100: '#BFDCC8',
  danger: '#C62828', danger50: '#FFEBEE', danger100: '#FECACA',
  amber: '#B45309', amber50: '#FEF3C7',
  yellow: '#A16207', yellow50: '#FEF9C3',
  blue: '#1D4ED8', blue50: '#DBEAFE',
  purple: '#6B21A8', purple50: '#F3E8FF',
  teal: '#0F766E', teal50: '#CCFBF1',
  bg: '#F8F9FA', surface: '#ffffff',
  ink: '#0f172a', ink2: '#334155', ink3: '#64748b', ink4: '#94a3b8',
  border: 'rgba(15,23,42,0.08)', borderStrong: 'rgba(15,23,42,0.14)',
  gray50: '#F1F5F9',
};

// ─── SVG Icon library ────────────────────────────────────────────────────
function Icon({ name, size = 16, stroke = 1.6, color, style }) {
  const p = {
    width: size, height: size, viewBox: '0 0 24 24',
    fill: 'none', stroke: color || 'currentColor',
    strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round', style,
  };
  const paths = {
    users:       <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    userCheck:   <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="m17 11 2 2 4-4"/></>,
    userPlus:    <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></>,
    alert:       <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></>,
    trending:    <><path d="M22 7 13.5 15.5 8.5 10.5 2 17"/><path d="M16 7h6v6"/></>,
    fingerprint: <><path d="M12 11a3 3 0 0 0-3 3 8 8 0 0 1-1 4"/><path d="M12 11v3a12 12 0 0 1-1 5"/><path d="M3 8a9 9 0 0 1 18 0"/><path d="M6 14a15 15 0 0 1-1 6"/><path d="M18 14a15 15 0 0 0 1 6"/><path d="M15 14a3 3 0 0 0-6 0"/></>,
    checkCircle: <><path d="M22 11v1a10 10 0 1 1-5.9-9.1"/><path d="m22 4-10 10-3-3"/></>,
    upload:      <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m17 8-5-5-5 5"/><path d="M12 3v12"/></>,
    wallet:      <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12h3v4h-3a2 2 0 0 1 0-4z"/></>,
    fileText:    <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h6M8 9h2"/></>,
    chevronR:    <><path d="m9 18 6-6-6-6"/></>,
    clock:       <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
    calendar:    <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    sun:         <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></>,
    creditCard:  <><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></>,
    activity:    <><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></>,
    banknote:    <><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></>,
  };
  return <svg {...p}>{paths[name]}</svg>;
}

// ─── Utilitaires ─────────────────────────────────────────────────────────
const toArray = d => Array.isArray(d) ? d : (d?.rows || d?.data || d?.employes || []);

const SITE_NOMS = {
  DAK: 'Dakar', THI: 'Thiès', KAO: 'Kaolack', STL: 'Saint-Louis',
  ZIG: 'Ziguinchor', TAM: 'Tambacounda', DIO: 'Diourbel', FAT: 'Fatick',
  MPAL: 'Mpal', DAKAR: 'Dakar', THIES: 'Thiès', KAOLACK: 'Kaolack',
  DIOURBEL: 'Diourbel', ZIGUINCHOR: 'Ziguinchor', LOUGA: 'Louga',
  'SG-SIEGE': 'Siège',
};

const fmtDate = d => {
  const jours = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
  const mois = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  return `${jours[d.getDay()]} ${d.getDate()} ${mois[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtFCFA = n => {
  if (!n && n !== 0) return '–';
  return new Intl.NumberFormat('fr-SN').format(n) + ' FCFA';
};

const fmtRelative = dateStr => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'il y a < 1h';
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
};

// ─── Sous-composants (HORS composant parent) ─────────────────────────────

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: '#fff', border: `0.5px solid ${T.border}`,
      borderRadius: 12, boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      overflow: 'hidden', ...style,
    }}>
      {children}
    </div>
  );
}

function CardHeader({ title, sub, right }) {
  return (
    <div style={{
      padding: '14px 18px', borderBottom: `0.5px solid ${T.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, margin: 0 }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function StatCard({ icon, tint, bg, label, value, sublabel }) {
  return (
    <div style={{
      background: '#fff', border: `0.5px solid ${T.border}`, borderRadius: 12,
      padding: '16px 18px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: bg, color: tint, display: 'grid', placeItems: 'center' }}>
          <Icon name={icon} size={17} />
        </div>
        <div style={{ fontSize: 11, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, textAlign: 'right', maxWidth: 80 }}>{label}</div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1, color: T.ink }}>{value}</div>
      <div style={{ fontSize: 12, color: T.ink3, display: 'flex', alignItems: 'center', gap: 6 }}>{sublabel}</div>
    </div>
  );
}

function AlertLine({ dot, level, range, count, leftBorder }) {
  return (
    <div style={{
      padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14,
      borderLeft: leftBorder ? `0.5px solid ${T.border}` : 'none',
    }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0, boxShadow: `0 0 0 3px ${dot}22` }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{level}</div>
        <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 1 }}>{range}</div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', color: T.ink, fontVariantNumeric: 'tabular-nums' }}>{count}</div>
    </div>
  );
}

function SiteBar({ code, nom, count, pct, emphasis }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr 64px', gap: 12, alignItems: 'center', padding: '8px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: T.ink2, letterSpacing: '0.02em' }}>
        {nom || SITE_NOMS[code] || code}
      </div>
      <div style={{ height: 8, background: T.gray50, borderRadius: 999, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${pct}%`,
          background: emphasis ? `linear-gradient(90deg, ${T.primary}, ${T.primary600})` : T.primary100,
          borderRadius: 999, transition: 'width 400ms ease',
        }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{count}</span>
        <span style={{ fontSize: 11, color: T.ink3, minWidth: 26, textAlign: 'right' }}>{pct}%</span>
      </div>
    </div>
  );
}

function QuickActionBtn({ icon, tint, bg, label, badge, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px', background: hovered ? T.primary50 : '#fff',
        border: `0.5px solid ${hovered ? T.primary100 : T.border}`,
        borderRadius: 10, textAlign: 'left', cursor: 'pointer',
        color: hovered ? T.primary : T.ink, position: 'relative',
        transition: 'all 150ms ease', minWidth: 0,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: hovered ? '#fff' : (bg || T.primary50),
        color: tint || T.primary,
        display: 'grid', placeItems: 'center', flexShrink: 0,
        transition: 'background 150ms ease',
      }}>
        <Icon name={icon} size={16} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, flex: 1, lineHeight: 1.25, paddingRight: badge != null ? 18 : 0 }}>
        {label}
      </div>
      {badge != null && (
        <span style={{
          position: 'absolute', top: 8, right: 8,
          minWidth: 18, height: 18, padding: '0 5px',
          background: T.danger, color: '#fff',
          fontSize: 10.5, fontWeight: 600, borderRadius: 999,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{badge}</span>
      )}
    </button>
  );
}

function AlertesRetraiteWidget({ alertes, navigate }) {
  if (!alertes) return null;
  const { critique, urgent, precoce, info, total } = alertes;
  if (total === 0) return null;

  return (
    <div style={{
      background: '#fff', border: `0.5px solid ${T.border}`,
      borderLeft: `4px solid ${T.danger}`, borderRadius: 12,
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)', overflow: 'hidden',
    }}>
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.danger50, color: T.danger, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon name="alert" size={18} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, margin: '0 0 2px' }}>Alertes retraite</div>
            <div style={{ fontSize: 12.5, color: T.ink3 }}>{total} collaborateur{total > 1 ? 's' : ''} approche{total > 1 ? 'nt' : ''} de l'âge légal de départ.</div>
          </div>
        </div>
        <button
          onClick={() => navigate('/provisions')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 500,
            color: T.primary, background: T.primary50, border: `0.5px solid ${T.primary100}`, cursor: 'pointer',
          }}
        >
          Voir la liste <Icon name="chevronR" size={13} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', borderTop: `0.5px solid ${T.border}` }}>
        <AlertLine dot={T.danger}  level="Critique" range="< 12 mois" count={critique} />
        <AlertLine dot={T.amber}   level="Urgent"   range="< 24 mois" count={urgent}   leftBorder />
        <AlertLine dot="#CA8A04"   level="Précoce"  range="< 36 mois" count={precoce}  leftBorder />
        <AlertLine dot={T.blue}    level="Info"     range="< 60 mois" count={info}     leftBorder />
      </div>
    </div>
  );
}

function StatsSection({ stats }) {
  const total = stats?.total || 1;
  const pct = Math.min(100, Math.max(0, stats ? Math.round((stats.actifs / total) * 100) : 0));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
      <StatCard
        icon="users" tint={T.primary} bg={T.primary50}
        label="Total employés"
        value={stats?.total ?? '–'}
        sublabel={<><span style={{ color: T.ink3 }}>/ 500 prévus</span><span style={{ background: T.primary50, color: T.primary, fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999, marginLeft: 4 }}>{Math.min(100, Math.round(total / 500 * 100))}%</span></>}
      />
      <StatCard
        icon="userCheck" tint={T.blue} bg={T.blue50}
        label="Actifs"
        value={stats?.actifs ?? '–'}
        sublabel={<><span style={{ width: 6, height: 6, borderRadius: '50%', background: T.primary, display: 'inline-block' }} /><span style={{ color: T.primary, fontWeight: 500, marginLeft: 4 }}>{pct}% du total</span></>}
      />
      <StatCard
        icon="alert" tint={T.ink3} bg={T.gray50}
        label="Inactifs"
        value={stats?.inactifs ?? '–'}
        sublabel={<span style={{ color: T.ink3 }}>{stats?.inactifs === 0 ? 'Aucun cas' : 'Suspendus / Retraités'}</span>}
      />
      <StatCard
        icon="trending" tint={T.amber} bg={T.amber50}
        label="Nouveaux (30 j)"
        value={stats?.nouveaux ?? '–'}
        sublabel={<span style={{ color: stats?.nouveaux > 0 ? T.primary : T.ink3, fontWeight: stats?.nouveaux > 0 ? 500 : 400 }}>{stats?.nouveaux > 0 ? `+${stats.nouveaux} ce mois` : 'Aucun ce mois'}</span>}
      />
    </div>
  );
}

// ─── KPI Cards (executive-kpis) ──────────────────────────────────────────

// Chantier temps (agent I) : UNE seule carte « Temps » (présence du jour, congés en attente), qui renvoie
// au menu « Temps » (/temps : tableau de bord du temps, à traiter, journées, clôture et paie).
function PresenceCard({ presence, conges, navigate }) {
  const { presents = 0, absents = 0, retards = 0, taux_presence = 0, total_actifs = 0 } = presence || {};
  const { en_attente = 0 } = conges || {};
  return (
    <Card>
      <CardHeader title="Temps" sub={`Présence du jour · ${fmtDate(new Date())}`} />
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <span style={{ fontSize: 28, fontWeight: 700, color: T.ink, letterSpacing: '-0.02em' }}>{presents}</span>
            <span style={{ fontSize: 14, color: T.ink3, marginLeft: 6 }}>présents</span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            {retards > 0 && (
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: T.amber }}>{retards}</span>
                <span style={{ fontSize: 11, color: T.ink3, marginLeft: 4 }}>retards</span>
              </div>
            )}
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: T.danger }}>{absents}</span>
              <span style={{ fontSize: 12, color: T.ink3, marginLeft: 4 }}>absents</span>
            </div>
          </div>
        </div>
        <div>
          <div style={{ height: 8, background: T.gray50, borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.min(100, Math.max(0, taux_presence))}%`,
              background: taux_presence >= 80
                ? `linear-gradient(90deg, ${T.primary}, ${T.primary600})`
                : taux_presence >= 60 ? '#CA8A04' : T.danger,
              borderRadius: 999, transition: 'width 600ms ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: T.ink3 }}>Taux de présence</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{taux_presence}%</span>
          </div>
        </div>
        {total_actifs === 0 && (
          <p style={{ fontSize: 11.5, color: T.ink4, margin: 0 }}>Aucun pointage enregistré aujourd'hui.</p>
        )}
        {conges && (
          <p style={{ fontSize: 12.5, color: T.ink2, margin: 0 }}>
            {en_attente} demande{en_attente !== 1 ? 's' : ''} de congé en attente de validation
          </p>
        )}
        {navigate && (
          <button
            onClick={() => navigate('/temps')}
            style={{
              alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12.5, color: T.primary, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            Ouvrir le suivi du temps <Icon name="chevronR" size={12} />
          </button>
        )}
      </div>
    </Card>
  );
}

function PretsCard({ prets }) {
  const { actifs = 0, en_cours = 0, soumis_dg = 0, en_attente_rh = 0, montant_restant_total = 0 } = prets || {};
  return (
    <Card>
      <CardHeader title="Prêts SOGAS" />
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: T.primary50, borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.primary }}>{actifs}</div>
            <div style={{ fontSize: 11, color: T.primary600, marginTop: 2 }}>prêts actifs</div>
          </div>
          <div style={{ flex: 1, background: T.blue50, borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.blue }}>{en_cours}</div>
            <div style={{ fontSize: 11, color: T.blue, marginTop: 2 }}>en traitement</div>
          </div>
        </div>
        {(soumis_dg > 0 || en_attente_rh > 0) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {soumis_dg > 0 && (
              <span style={{ fontSize: 11, background: T.danger50, color: T.danger, padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                {soumis_dg} en attente DG
              </span>
            )}
            {en_attente_rh > 0 && (
              <span style={{ fontSize: 11, background: '#FFF3E0', color: '#E65100', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                {en_attente_rh} à traiter RH
              </span>
            )}
          </div>
        )}
        <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 10 }}>
          <div style={{ fontSize: 11, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Capital restant dû</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>{fmtFCFA(montant_restant_total)}</div>
        </div>
      </div>
    </Card>
  );
}

function MasseSalarialeCard({ masseSalariale }) {
  const { estimation = 0, note } = masseSalariale || {};
  return (
    <Card>
      <CardHeader title="Masse salariale" sub="Estimation mensuelle" />
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: T.teal50, color: T.teal, display: 'grid', placeItems: 'center' }}>
            <Icon name="banknote" size={20} />
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, letterSpacing: '-0.01em' }}>{fmtFCFA(estimation)}</div>
            <div style={{ fontSize: 11, color: T.ink3 }}>/ mois</div>
          </div>
        </div>
        {note && (
          <p style={{ fontSize: 11, color: T.ink4, margin: 0, borderTop: `0.5px solid ${T.border}`, paddingTop: 8 }}>{note}</p>
        )}
      </div>
    </Card>
  );
}

function DettesAutresCard({ dettesAutres }) {
  const { nb = 0, montant_total = 0 } = dettesAutres || {};
  return (
    <Card>
      <CardHeader title="Dettes actives" sub="OS · avances · cautions" />
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: T.amber50, borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.amber }}>{nb}</div>
            <div style={{ fontSize: 11, color: T.amber, marginTop: 2 }}>dossiers actifs</div>
          </div>
        </div>
        <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 10 }}>
          <div style={{ fontSize: 11, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Montant total restant</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>{fmtFCFA(montant_total)}</div>
        </div>
      </div>
    </Card>
  );
}

function DocumentsCard({ documents }) {
  const { a_renouveler, note } = documents || {};
  return (
    <Card>
      <CardHeader title="Documents à renouveler" />
      <div style={{ padding: '16px 18px' }}>
        {a_renouveler === null ? (
          <p style={{ fontSize: 12.5, color: T.ink4, margin: 0 }}>{note || 'Module en cours de configuration.'}</p>
        ) : (
          <div style={{ fontSize: 24, fontWeight: 700, color: T.ink }}>{a_renouveler}</div>
        )}
      </div>
    </Card>
  );
}

function RepartitionSitesCard({ parSite, total }) {
  const sites = toArray(parSite);
  const max = sites.length > 0 ? Math.max(...sites.map(s => s.total)) : 1;
  return (
    <Card>
      <CardHeader
        title="Répartition par site"
        sub={`${total} collaborateurs actifs`}
      />
      <div style={{ padding: '6px 18px 18px' }}>
        {sites.length === 0
          ? <p style={{ fontSize: 13, color: T.ink3, textAlign: 'center', margin: '12px 0' }}>Aucune donnée</p>
          : sites.map((s, i) => (
            <SiteBar
              key={s.site_id || s.site}
              code={s.site}
              nom={s.site_nom}
              count={s.total}
              pct={total > 0 ? Math.round(s.total / total * 100) : 0}
              emphasis={i === 0}
            />
          ))
        }
      </div>
    </Card>
  );
}

function AccesRapidesCard({ role, navigate, soumis_dg = 0 }) {
  const isRH = ['drh', 'rh_principal', 'assistant_rh'].includes(role);
  const isDirection = ['admin', 'directeur_general', 'directeur_adjoint'].includes(role);

  const actionsDirection = [
    { icon: 'creditCard', tint: T.danger,   bg: T.danger50,  label: 'Prêts à approuver', badge: soumis_dg > 0 ? soumis_dg : null, onClick: () => navigate('/prets-sogas') },
    { icon: 'users',      tint: T.primary,  bg: T.primary50, label: 'Gestion effectifs',  onClick: () => navigate('/employes') },
    { icon: 'wallet',     tint: T.amber,    bg: T.amber50,   label: 'Provisions retraite', onClick: () => navigate('/provisions') },
    { icon: 'activity',   tint: T.purple,   bg: T.purple50,  label: 'Journal d\'activité', onClick: () => navigate('/audit') },
  ];

  const actionsRH = [
    { icon: 'userPlus',    tint: T.primary, bg: T.primary50, label: 'Nouvel employé',     onClick: () => navigate('/employes/nouveau') },
    { icon: 'fingerprint', tint: T.purple,  bg: T.purple50,  label: 'Pointage du jour',   onClick: () => navigate('/pointage') },
    { icon: 'checkCircle', tint: T.primary, bg: T.primary50, label: 'Congés à valider',   onClick: () => navigate('/conges') },
    { icon: 'upload',      tint: T.blue,    bg: T.blue50,    label: 'Import Excel',        onClick: () => navigate('/parametres') },
    { icon: 'wallet',      tint: T.amber,   bg: T.amber50,   label: 'Provisions retraite', onClick: () => navigate('/provisions') },
    { icon: 'fileText',    tint: T.ink3,    bg: T.gray50,    label: 'Rapport mensuel',     onClick: () => navigate('/temps/mensuel') },
  ];

  const actionsTerrain = [
    { icon: 'users',       tint: T.primary, bg: T.primary50, label: 'Mon équipe',         onClick: () => navigate('/employes') },
    { icon: 'fingerprint', tint: T.purple,  bg: T.purple50,  label: 'Pointage du jour',   onClick: () => navigate('/pointage') },
    { icon: 'checkCircle', tint: T.primary, bg: T.primary50, label: 'Congés à valider',   onClick: () => navigate('/conges') },
    { icon: 'calendar',    tint: T.blue,    bg: T.blue50,    label: 'Gestion du temps',   onClick: () => navigate('/temps') },
  ];

  const actions = isDirection ? actionsDirection : (isRH ? actionsRH : actionsTerrain);

  return (
    <Card>
      <CardHeader title="Accès rapides" sub="Raccourcis du quotidien RH" />
      <div style={{ padding: '6px 18px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {actions.map(a => (
          <QuickActionBtn key={a.label} {...a} />
        ))}
      </div>
    </Card>
  );
}

function ActivitesCard({ activites }) {
  const items = activites || [];

  const actionLabel = action => {
    if (action === 'INSERT') return { label: 'Création', color: T.primary, bg: T.primary50 };
    if (action === 'UPDATE') return { label: 'Modification', color: T.blue, bg: T.blue50 };
    if (action === 'GENERER_PDF') return { label: 'PDF', color: T.purple, bg: T.purple50 };
    if (action === 'CONSULTATION') return { label: 'Consultation', color: T.ink3, bg: T.gray50 };
    return { label: action, color: T.ink3, bg: T.gray50 };
  };

  return (
    <Card>
      <CardHeader title="Activité récente" sub="Dernières actions sur le système" />
      {items.length === 0 ? (
        <div style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: T.gray50, color: T.ink4, display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
            <Icon name="clock" size={20} />
          </div>
          <p style={{ fontSize: 13.5, color: T.ink3, margin: '0 0 4px', fontWeight: 500 }}>Aucune activité récente</p>
          <p style={{ fontSize: 12, color: T.ink4, margin: 0 }}>Le journal sera alimenté au fil des actions.</p>
        </div>
      ) : (
        <div style={{ padding: '4px 0 8px' }}>
          {items.map((a, i) => {
            const badge = actionLabel(a.action);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 18px',
                borderBottom: i < items.length - 1 ? `0.5px solid ${T.border}` : 'none',
              }}>
                <span style={{
                  flexShrink: 0, marginTop: 2,
                  fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 6,
                  background: badge.bg, color: badge.color,
                }}>{badge.label}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: T.ink, margin: 0, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.description}
                  </p>
                  <p style={{ fontSize: 11.5, color: T.ink3, margin: '2px 0 0' }}>
                    {a.par} · {fmtRelative(a.date)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────
export default function DashboardPage() {
  const { employe } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [alertes, setAlertes] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('30 j');
  const role = employe?.role || 'employe';

  useEffect(() => {
    Promise.all([
      axios.get('/api/dashboard'),
      axios.get('/api/dashboard/alertes-retraite'),
      axios.get('/api/dashboard/executive-kpis'),
    ])
      .then(([r1, r2, r3]) => {
        setData(r1.data);
        setAlertes(r2.data);
        setKpis(r3.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const aujourd_hui = fmtDate(new Date());

  const titreHeader = {
    admin: 'Administration Système',
    directeur_general: 'Tableau de bord Direction',
    directeur_adjoint: 'Tableau de bord Direction',
    drh: 'Tableau de bord DRH',
    rh_principal: 'Tableau de bord RH',
    assistant_rh: 'Tableau de bord RH',
    comptable: 'Tableau de bord Comptabilité',
    // Module 0 : site_id est un UUID — on affiche le nom du site fourni par l'API, jamais l'UUID.
    prepose_regional: `Mon Abattoir${employe?.site ? ' · ' + (employe.site.nom || employe.site.code) : ''}`,
    chef_departement: `Mon Département${employe?.departement ? ' · ' + employe.departement : ''}`,
    chef_service: `Mon Service${employe?.service ? ' · ' + employe.service : ''}`,
    chef_equipe: `Mon Équipe${employe?.service ? ' · ' + employe.service : ''}`,
  }[role] || 'Tableau de bord RH';

  const isDirection = ['admin', 'directeur_general', 'directeur_adjoint'].includes(role);
  const isRH = ['drh', 'rh_principal', 'assistant_rh', 'comptable'].includes(role);
  const isTerrain = ['prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'].includes(role);
  const showExecutiveKpis = isDirection || isRH;

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <p style={{ color: '#888', fontSize: 14, fontFamily: 'system-ui, sans-serif' }}>Chargement…</p>
      </div>
    </Layout>
  );

  const total = data?.stats?.total || 0;
  const totalActifsKpi = kpis?.employes?.total || 0;
  const parSite = kpis?.employes?.par_site || toArray(data?.parSite);
  const parSiteTotal = showExecutiveKpis ? totalActifsKpi : total;

  return (
    <Layout>
      <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: T.bg }}>

        {/* ── Titre de page (refonte visuelle : le logo et la cloche sont dans l'en-tête général) ── */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px 0' }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: 'var(--sg-texte)', fontFamily: 'var(--sg-police)' }}>
            Bonjour, {employe?.prenom || 'utilisateur'}.
          </h1>
          <p style={{ margin: '2px 0 0', color: 'var(--sg-texte-3)', fontSize: 13, fontFamily: 'var(--sg-police)' }}>
            <span style={{ textTransform: 'capitalize' }}>{aujourd_hui}</span> · {titreHeader}
          </p>
        </div>

        {/* ── Corps principal ── */}
        <div style={{ padding: '20px 16px 48px', display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1200, margin: '0 auto' }}>

          {/* Période toggle */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: T.ink3 }}>Période</span>
            <div style={{ display: 'inline-flex', background: '#fff', border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: 3, gap: 2 }}>
              {['7 j', '30 j', 'Trim.', 'Année'].map(p => (
                <button
                  key={p}
                  onClick={() => setPeriode(p)}
                  style={{
                    padding: '5px 10px', borderRadius: 6, fontSize: 12.5, fontWeight: 500,
                    background: periode === p ? T.primary50 : 'transparent',
                    color: periode === p ? T.primary : T.ink3,
                    border: 'none', cursor: 'pointer',
                  }}
                >{p}</button>
              ))}
            </div>
          </div>

          {/* Section 1 — Stats employés (existant) */}
          <StatsSection stats={data?.stats} />

          {/* Section 2 — Alertes retraite */}
          {(isDirection || isRH) && alertes && alertes.total > 0 && (
            <AlertesRetraiteWidget alertes={alertes} navigate={navigate} />
          )}

          {/* Section 3 — KPIs financiers DG en premier */}
          {isDirection && kpis && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <PretsCard prets={kpis.prets} />
                <DettesAutresCard dettesAutres={kpis.dettes_autres} />
                <MasseSalarialeCard masseSalariale={kpis.masse_salariale} />
                <PresenceCard presence={kpis.presence} conges={kpis.conges} navigate={navigate} />
              </div>
            </>
          )}

          {/* Section 3 — Actions du jour RH en premier */}
          {isRH && kpis && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <PresenceCard presence={kpis.presence} conges={kpis.conges} navigate={navigate} />
              <PretsCard prets={kpis.prets} />
              <MasseSalarialeCard masseSalariale={kpis.masse_salariale} />
            </div>
          )}

          {/* Section 3 — Présence équipe pour managers */}
          {isTerrain && kpis && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <PresenceCard presence={kpis.presence} conges={kpis.conges} navigate={navigate} />
            </div>
          )}

          {/* Section 4 — Sites + Accès rapides (Direction/RH) */}
          {(isDirection || isRH) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 14 }}>
              <RepartitionSitesCard parSite={parSite} total={parSiteTotal} />
              <AccesRapidesCard role={role} navigate={navigate} soumis_dg={kpis?.prets?.soumis_dg || 0} />
            </div>
          )}

          {/* Terrain : colonne unique */}
          {isTerrain && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <RepartitionSitesCard parSite={parSite} total={parSiteTotal} />
              <AccesRapidesCard role={role} navigate={navigate} />
            </div>
          )}

          {/* Section 5 — Activités récentes */}
          <ActivitesCard activites={kpis?.activites} />

          {/* Types de contrats */}
          {(isDirection || isRH) && toArray(data?.parContrat).length > 0 && (
            <Card>
              <CardHeader title="Types de contrats" />
              <div style={{ padding: '6px 18px 18px' }}>
                {toArray(data.parContrat).map(c => (
                  <div key={c.contrat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `0.5px solid ${T.border}`, marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: T.ink2 }}>{c.contrat || '–'}</span>
                    <span style={{ background: T.primary50, color: T.primary, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{c.total}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <p style={{ textAlign: 'center', fontSize: '0.6rem', color: '#bbb', margin: '4px 0 0' }}>
            © {new Date().getFullYear()} SOGAS — Tous droits réservés
          </p>
        </div>
      </div>
    </Layout>
  );
}
